// A home for common styles, colors, sizes, design elements, etc
import { capitalize } from 'core/utils';

// Name the color for itself, not for how it's used.
// Generate color names here if you want: http://chir.ag/projects/name-that-color/
export const colors = {
	cjAdmin: '#E15596',
	cratejoyTeal: '#39C0C3',
	// Grays, all of them...
	alto: '#e0e0e0',
	dustyGray: '#9b9b9b',
	gray: '#333',
	mutedGray: '#858585',
	gray0: '#f8f8f8',
	gray1: '#ededed',  // accessibility issues on white
	gray2: '#d9d9d9',  // accessibility issues on white
	gray3: '#c8c8c8',  // accessibility issues on white
	gray4: '#b7b7b7',  // accessibility issues on white
	gray5: '#6c6c6c',
	gray8: '#8a8a8a',  // accessibility issues on white
	gray9: '#5c5c5c',
	gray10: '#8b8b8b',
	gray11: '#999',
	shuttleGray: '#586974',
	concreteGray: '#f3f3f3',
	geyser: '#d3dde4',
	whisper: '#e8e5f1',
	blueHaze: '#d1cbe3',
	wildSand: '#f6f5f5',
	muteBlue: '#ebf8f9',
	mercury: '#e7e7e7',
	offGreen: '#ECFAF4',
	lightMint: '#d3f0e1',
	mineralGreenGray: '#3E5758',
	outerSpaceGray: '#243637',
	limedSpruceGray: '#334849',
	limedHover: '#526c6d',
	jungleMist: '#C3D4DA',
	mystic: '#e8edf1',
	cuttySark: '#52696A',
	cosmos: '#fed9d7',
	primaryBrand: '#38bfc3',
	primaryBrandHover: '#52c5c8',
	primaryBrandLight: '#d5efef',
	porcelain: '#f8fafa',
	salmon: '#ff5d52',
	roman: '#db675f',
	shakespeare: '#44bdd5',
	geraldine: '#f69a95',  // "CJ-Pink" From holiday design contractor
	parchment: '#f3ebd8',
	feijoa: '#95d272',
	malachite: '#0CAC3F',
	mountainMeadow: '#14CB73',
	mantis: '#90C96B',
	zuccini: '#075F34',
	gold: '#fcb53e',
	lavender: '#8b7dba',
	blueBell: '#a297c7',
	prelude: '#bfb4de',
	white: '#ffffff',
	black: '#333',
	bridesmaid: '#feecea',
	sangria: '#9D0601', // Has the appropriate contrast to be foreground of bridesmaid 
	morningGlory: '#99e0e2',
	merino: '#f1ebd9',
	fountainBlue: '#51bdc1',
	burntSienna: '#e68d51',
	cornflowerBlue: '#6366F1',
	facebook: '#4c73cb',
	facebookHover: '#3d63b8',
	google: '#f06554',
	googleHover: '#e44f3d',
	cinnabar: '#e03d2b',
	grayAlpha: 'rgba(0, 0, 0, 0.25)',
	albescentWhite: '#f4eacb',
	yellowMetal: '#7b693d',
	rustyNail: '#8E6106',
	// Blues
	pelorous: '#38BFC3',
	darkTeal: '#268285',
	brightTurquoise: '#07D0D6',
	easternBlue: '#24ADB1',
	lightTeal: '#EDF9F9',
	// Reds
	redOrange: '#F94532',
	carnation: '#FA5F4E',
	mulberry: '#bd4198',
	sunsetOrange: '#f64240',
	holidayRed: '#EC1518',
	salem: '#0A8933',
	scandal: '#D1FAE5',
	watercourse: '#065F46',
	deepOrange: '#92400E',
	lightYellow: '#FEF3C7',
	oldBrick: '#991B1B',
	lightRed: '#FEE2E2',
	// Grays used on Chips
	mishka: '#D1D5DB',
	oxfordBlue: '#374151',
	royalBlue: '#4F46E5',
	emperor: '#4F4C4D',
	greenHaze: '#059669',
};

export const Colors = {
	...colors,
	textPrimary: colors.gray,
	textFade: colors.dustyGray,
	textLight: colors.white,
	textDanger: colors.salmon,

	border: colors.alto,

	active: colors.pelorous,
	activeLight: colors.brightTurquoise,
	inactive: colors.alto,

	positive: colors.pelorous,
	positiveLight: colors.brightTurquoise,
	positiveDark: colors.easternBlue,

	danger: colors.redOrange,
	dangerLight: colors.carnation,
	dangerDark: colors.cinnabar,

	dangerAlt: colors.bridesmaid,

	purchase: colors.malachite,
	purchaseLight: colors.mountainMeadow,
	purchaseDark: colors.salem,

	gift: colors.lavender,
	giftLight: colors.blueBell,
	giftDark: colors.lavender,

	neutral: colors.gray,
	neutralLight: colors.mineralGreenGray,
	neutralDark: colors.outerSpaceGray,

	// Grays used on Chips
	chipBorderGray: colors.mishka,
	chipTextGray: colors.oxfordBlue,
};

export const getColor = color => {
	const match = Colors[color];
	if (match) return match;
	throw (new Error(`Color "${color}" is not defined`));
};

const styleVarNumbers = {
	lineHeightBase: 1.4,
	defaultSpacingUnit: 16,
};

const mobileFirstBreakPoints = {
	cancelBreakpointMd: '650px',
	containerSm: '750px',
	screenMin: '360px',
	screenXsm: '480px',
	screenSm: '768px',
	screenMd: '992px',
	screenCjTablet: '1024px',
	screenCjDesktopSm: '1200px',
	screenCjDesktopMd: '1366px',
	screenCjDesktopLg: '1440px',
	screenCjDesktopXl: '1600px',
	screenCjDesktopXxl: '1920px',
	screenCjMinMaxContainer: '1160px',
};

const desktopFirstBreakPoints = {};

for (const key in mobileFirstBreakPoints) {
	const desktopSize = Number(mobileFirstBreakPoints[key].split("px")[0] - 1);
	const newKey = `desktop${capitalize(key)}`;
	desktopFirstBreakPoints[newKey] = `${desktopSize}px`;
}

export const StyleVars = {
	defaultSpacingUnit: styleVarNumbers.defaultSpacingUnit,
	lineHeightBase: styleVarNumbers.lineHeightBase,
	defaultBorderRadius: '4px',
	fixedwidthMax: '1320px',
	...mobileFirstBreakPoints,
	...desktopFirstBreakPoints,
};

export const Fonts = {
	/* base is default and therefore shouldn't need specified much */
	system: `-apple-system, system-ui, BlinkMacSystemFont, serif`,
	base: '"Muli", -apple-system, system-ui, BlinkMacSystemFont, "Open Sans", sans-serif',
	serif: '"Lora", Georgia, "Times New Roman", Times, serif',
};
